// Format a Date's LOCAL calendar day as YYYY-MM-DD. A DOB is a calendar date,
// not an instant, so we must read local components. Using toISOString() here
// would shift the day for users east/west of UTC (e.g. a date picked at local
// midnight in IST serializes to the previous day at 18:30 UTC).
const toLocalIsoDate = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const ConvertDOBToYYYYMMDD = (dob: any): string | null => {
  if (!dob) return null;

  // If it's already a Date object, read its local calendar day.
  if (dob instanceof Date) {
    return isNaN(dob.getTime()) ? null : toLocalIsoDate(dob);
  }

  if (typeof dob !== "string") return null;

  const trimmed = dob.trim();

  // ISO instant with time+zone (e.g. 2008-08-26T18:30:00.000Z). Parse the
  // instant and read the LOCAL day so the calendar date the user picked is
  // preserved instead of the UTC day.
  if (trimmed.includes("T")) {
    const parsed = new Date(trimmed);
    // Unparseable instant: return null rather than a malformed non-ISO string,
    // so callers can rely on the documented "YYYY-MM-DD | null" output shape.
    return isNaN(parsed.getTime()) ? null : toLocalIsoDate(parsed);
  }

  // Already in ISO format YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  // Year-first with slashes YYYY/MM/DD (how minor profiles are stored)
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(trimmed)) {
    return trimmed.replace(/\//g, "-");
  }

  // Handle MM/DD/YYYY, DD/MM/YYYY, MM-DD-YYYY, or DD-MM-YYYY
  if (/^\d{1,2}[/-]\d{1,2}[/-]\d{4}$/.test(trimmed)) {
    const parts = trimmed.split(/[/-]/);
    const p0 = parts[0];
    const p1 = parts[1];
    const yyyy = parts[2];

    const n0 = parseInt(p0, 10);
    const n1 = parseInt(p1, 10);

    if (n1 > 12) {
      // p1 > 12 means format is MM-DD-YYYY (e.g. 10-30-1983)
      return `${yyyy}-${p0.padStart(2, "0")}-${p1.padStart(2, "0")}`;
    } else if (n0 > 12) {
      // p0 > 12 means format is DD-MM-YYYY (e.g. 30-10-1983)
      return `${yyyy}-${p1.padStart(2, "0")}-${p0.padStart(2, "0")}`;
    } else {
      // Default to MM-DD-YYYY (API standard format)
      return `${yyyy}-${p0.padStart(2, "0")}-${p1.padStart(2, "0")}`;
    }
  }

  return trimmed;
};

export const parseMinorProfileDobToIso = (dob: unknown): string => {
  if (!dob) return "";
  // Date / ISO-with-time: read the LOCAL calendar day (toLocalIsoDate), same as
  // ConvertDOBToYYYYMMDD, so a DOB picked at local midnight isn't shifted back
  // a day by UTC serialization.
  if (dob instanceof Date) {
    return isNaN(dob.getTime()) ? "" : toLocalIsoDate(dob);
  }
  const trimmed = String(dob).trim();

  if (trimmed.includes("T")) {
    const parsed = new Date(trimmed);
    return isNaN(parsed.getTime()) ? "" : toLocalIsoDate(parsed);
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  // Stored as YYYY/MM/DD by some minor-profile writers.
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(trimmed)) return trimmed.replace(/\//g, "-");

  // DD/MM/YYYY (or DD-MM-YYYY) — day first, unambiguously.
  const match = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(trimmed);
  if (match) {
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }
  return "";
};

// Earliest DOB that still counts as a minor: one day after "18 years ago
// today". Someone born exactly 18 years ago has turned 18, so the DOB picker's
// minDate should start the day after — keeping the picker's selectable range in
// exact agreement with isMinorByDob's exclusive boundary.
export const getMinorDobMinDate = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear() - 18, now.getMonth(), now.getDate() + 1);
};

// True when the DOB belongs to someone who has NOT yet turned 18 today.
// Someone born exactly 18 years ago today HAS turned 18, so the boundary is
// exclusive: a minor's DOB must be strictly AFTER the date 18 years ago.
// Returns false for unparseable input.
export const isMinorByDob = (dob: unknown): boolean => {
  // Funnel through the day-first parser: DOB values are day-first when slashed
  // (DD/MM/YYYY), so a raw "25/12/2010" resolves correctly rather than being
  // mis-read as month-first. Also normalizes Date/ISO/YYYY-MM-DD/YYYY/MM/DD.
  const iso = parseMinorProfileDobToIso(dob);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;

  const [year, month, day] = iso.split("-").map((part) => parseInt(part, 10));
  const birth = new Date(year, month - 1, day);
  if (
    birth.getFullYear() !== year ||
    birth.getMonth() !== month - 1 ||
    birth.getDate() !== day
  ) {
    return false;
  }

  const now = new Date();
  const eighteenYearsAgo = new Date(
    now.getFullYear() - 18,
    now.getMonth(),
    now.getDate(),
  );
  // Strictly after the threshold: born exactly 18 years ago today => turned 18
  // today => NOT a minor.
  return birth.getTime() > eighteenYearsAgo.getTime();
};

// Single source of truth for age at "today" used by the adult (is-adult) check.
// Funnels through parseMinorProfileDobToIso so DOB strings — including day-first
// slashed dates and Date/ISO instants — are normalized to a local YYYY-MM-DD
// consistently (no month-first ambiguity, no UTC day-shift), then computes age
// with a calendar-correct month/day comparison. Returns null when unparseable.
export const getAgeFromDob = (dob: unknown): number | null => {
  const iso = parseMinorProfileDobToIso(dob);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;

  const [year, month, day] = iso.split("-").map((part) => parseInt(part, 10));
  // Build the date from explicit parts (local time) to avoid the UTC-midnight
  // shift that `new Date("YYYY-MM-DD")` introduces near timezone boundaries.
  const birthDate = new Date(year, month - 1, day);
  if (
    birthDate.getFullYear() !== year ||
    birthDate.getMonth() !== month - 1 ||
    birthDate.getDate() !== day
  ) {
    return null; // e.g. 2023-02-30 normalized to an invalid calendar date
  }

  const today = new Date();
  let age = today.getFullYear() - year;
  const monthDiff = today.getMonth() - (month - 1);
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
    age--;
  }
  return age;
};

export const autoFormatDateInput = (input: string): string => {
  if (!input) return "";
  const cleaned = input.replace(/\D/g, "").slice(0, 8);
  if (cleaned.length <= 2) {
    return cleaned;
  }
  if (cleaned.length <= 4) {
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
  }
  return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4)}`;
};

export const parseDobInputToIso = (dobStr: string): string | null => {
  if (!dobStr) return null;
  const trimmed = dobStr.trim();

  if (trimmed.includes("T")) {
    return trimmed.split("T")[0];
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  if (/^\d{1,2}[/-]\d{1,2}[/-]\d{4}$/.test(trimmed)) {
    const parts = trimmed.split(/[/-]/);
    let day = parseInt(parts[0], 10);
    let month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    if (month > 12 && day <= 12) {
      const temp = day;
      day = month;
      month = temp;
    }

    if (
      month >= 1 &&
      month <= 12 &&
      day >= 1 &&
      day <= 31 &&
      year >= 1900 &&
      year <= new Date().getFullYear()
    ) {
      const dd = String(day).padStart(2, "0");
      const mm = String(month).padStart(2, "0");
      return `${year}-${mm}-${dd}`;
    }
  }

  return null;
};
